import styled, { css } from 'styled-components';
import { ButtonShapes, IndividualButtonSizes, ButtonStyles } from './button.types';
import COLORS from '../../style/universal.color';
import { MLBTheme } from '../../style/theme.types';

interface StyledButtonProps {
  sizeStyles: IndividualButtonSizes;
  hSpeed: number;
  shape: ButtonShapes;
  backgroundColor?: string;
  borderless: boolean;
  borderColor?: string;
  onlyIcon: boolean;
  color?: string;
  iconColor?: string;
  icon: boolean;
  iconLeft: boolean;
  disabled: boolean;
  hasHover: boolean;
  hoverBackgroundColor?: string;
  hoverColor?: string;
  hoverBorderColor?: string;
  iconHoverColor?: string;
  theme: MLBTheme;
  styleName: ButtonStyles;
  active: boolean;
}

export const Label = styled.span`
  display: inline-block;
`;

const hoverStyles = css<StyledButtonProps>`
  ${({ disabled }) => (!disabled ? 'cursor: pointer;' : '')}
  ${({ shape, theme }) =>
    shape === ButtonShapes.TAB ? `border-bottom: 3px solid ${theme.primaryLinkHoverColor};` : ''}
  ${({ hasHover, hoverBackgroundColor, hoverColor, borderless, hoverBorderColor, iconHoverColor, iconColor, color }) =>
    hasHover
      ? `
    ${hoverBackgroundColor ? `background-color: ${hoverBackgroundColor};` : ''}
    ${hoverColor ? `color: ${hoverColor};` : ''}
    ${!borderless && hoverBorderColor ? `border: 1px solid ${hoverBorderColor};` : ''}
    svg {
      fill: ${iconHoverColor || iconColor || color};
      path {
        fill: ${iconHoverColor || iconColor || color};
      }
    }
  `
      : ''}
`;

export const StyledButton = styled.button<StyledButtonProps>`
  height: ${({ sizeStyles }) => sizeStyles.height}px;
  padding-left: ${({ sizeStyles }) => sizeStyles.horizontalPadding}px;
  padding-right: ${({ sizeStyles }) => sizeStyles.horizontalPadding}px;
  font-size: ${({ sizeStyles }) => sizeStyles.fontSize}px;

  ${({ hasHover, hSpeed }) => (hasHover ? `transition: ${hSpeed}s;` : '')}
  border-radius: ${({ shape, sizeStyles }) =>
    shape === ButtonShapes.TAB
      ? '0px'
      : [ButtonShapes.PILL, ButtonShapes.PILL_SOLID].includes(shape)
        ? `${sizeStyles.height / 2}px`
        : '5px'};
  font-weight: bold;
  vertical-align: bottom;

  ${({ backgroundColor, active, hoverBackgroundColor }) => {
    const color = active && hoverBackgroundColor ? hoverBackgroundColor : backgroundColor;

    return color ? `background-color: ${color};` : '';
  }}
  ${({ borderless, borderColor, onlyIcon, active, hoverBorderColor }) => {
    const color = active && hoverBorderColor ? hoverBorderColor : borderColor;

    return !borderless && color && !(ButtonShapes.PILL && onlyIcon) ? `border: 1px solid ${color};` : `border: none;`;
  }}
  ${({ color, active, hoverColor }) => {
    const btnColor = active && hoverColor ? hoverColor : color;

    return btnColor ? `color: ${btnColor};` : '';
  }}
  svg {
    padding-top: ${({ sizeStyles }) => sizeStyles.iconPaddingTop}px;
    vertical-align: top;
    fill: ${({ iconColor, color }) => iconColor || color};
    path {
      fill: ${({ iconColor, color }) => iconColor || color};
    }
  }
  ${Label} {
    ${({ icon, onlyIcon, iconLeft }) => (icon && iconLeft && !onlyIcon ? 'margin-left: 5px;' : '')}
    ${({ icon, onlyIcon, iconLeft }) => (icon && !iconLeft && !onlyIcon ? 'margin-right: 5px;' : '')}
    font-family: ${({ theme = {} }) => theme.fonts?.family?.primary};
  }
  // Media query to detect if the device has hover capability
  // This prevents hover styles from being applied on touch devices
  // but still allows :active styles to be applied
  @media (hover: hover) {
    &:hover {
      ${hoverStyles}
    }
  }
  &:active {
    ${hoverStyles}
  }
  ${({ shape, theme }) =>
    shape === ButtonShapes.TAB
      ? `
    background: transparent;
    border: 0;
    border-bottom: 3px solid ${COLORS.transparent};
    color: ${theme.disabledLinkColor};
    margin-right: 5px;
    margin-top: 6px;
    padding: 6px 12px;
    position: relative;
  `
      : ''}
  ${({ styleName, theme }) =>
    styleName === ButtonStyles.SELECTED_TAB
      ? `
    color: ${theme?.primaryTextColor};
    border-bottom: 3px solid ${theme.primaryLinkColor};
  `
      : ''}
`;
